import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useState } from 'react'
import { useEthers } from '../../hooks'
import { parseUnits } from '@ethersproject/units'
import { getGasTipFromGasPrice } from '../../utils'
import { ChainId } from '../../constants'
import { Web3Provider } from '@ethersproject/providers'

const MINUTE = 60 * 1000

interface GasPriceState {
  [chainId: number]:
    | {
        fast: BigNumber
        average: BigNumber
        slow: BigNumber
        baseFee: BigNumber
      }
    | undefined
}

export function useNetworkGasPrice() {
  const { chainId = ChainId.Mainnet, library } = useEthers()
  const [prices, setPrices] = useState<GasPriceState>({})

  useEffect(() => {
    let cancelled = false
    async function updateGasPrice() {
      if (library && chainId) {
        const gasPrice =
          chainId === ChainId.Mainnet
            ? (await getGasPriceFromEtherscan()) || (await getGasPriceFromEtherChain())
            : await getGasPriceFromProvider(library)
        if (!cancelled) {
          setPrices((prices) => ({ ...prices, [chainId]: gasPrice }))
        }
      }
    }
    updateGasPrice()
    const interval = setInterval(updateGasPrice, MINUTE)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [library, chainId])

  return chainId && prices[chainId]
}

async function getGasPriceFromProvider(library: Web3Provider) {
  const { maxFeePerGas, maxPriorityFeePerGas } = await library.getFeeData()
  if (maxFeePerGas && maxPriorityFeePerGas) {
    const baseFee = maxFeePerGas.sub(maxPriorityFeePerGas).div(2)
    return {
      fast: maxPriorityFeePerGas,
      average: maxPriorityFeePerGas,
      slow: maxPriorityFeePerGas,
      baseFee,
    }
  }
}

async function getGasPriceFromEtherscan() {
  try {
    const response = await fetch(
      'https://api.etherscan.io/api?module=gastracker&action=gasoracle&apikey=CN7J9CFZISN6NXMSZHW13E49CZE7AZ5IAX',
      { method: 'GET' }
    )
    const { FastGasPrice, ProposeGasPrice, SafeGasPrice, suggestBaseFee } = (await response.json()).result
    const baseFee = parseUnits(suggestBaseFee, 'gwei')
    return {
      fast: getGasTipFromGasPrice(parseUnits(FastGasPrice, 'gwei'), baseFee),
      average: getGasTipFromGasPrice(parseUnits(ProposeGasPrice, 'gwei'), baseFee),
      slow: getGasTipFromGasPrice(parseUnits(SafeGasPrice, 'gwei'), baseFee),
      baseFee,
    }
  } catch (err) {
    console.error(err)
  }
}

async function getGasPriceFromEtherChain() {
  try {
    const response = await fetch('https://www.etherchain.org/api/gasPriceOracle', { method: 'GET' })
    const { safeLow, standard, fast, currentBaseFee } = await response.json()
    return {
      fast: parseUnits((fast + currentBaseFee).toFixed(2), 'gwei'),
      average: parseUnits((standard + currentBaseFee).toFixed(2), 'gwei'),
      slow: parseUnits((safeLow + currentBaseFee).toFixed(2), 'gwei'),
      baseFee: parseUnits(currentBaseFee.toFixed(2), 'gwei'),
    }
  } catch (err) {
    console.error(err)
  }
}
